import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  LabelList
} from 'recharts';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '../chart';
import RoundedBackground from '../RoundedBackgroundCharts';

export default function SingleLineCharts({
  data,
  dataKey = 'week',
  valueKey = 'value',
  minWidthMultiplier = 65,
  isExpansionNeeded = false,
  tickFormatter = '%',
  left = 0,
  nearest5 = false,
  tooltipFormatter = (value) => value,
}) {
  if (!Array.isArray(data)) return null;

  const minimumWidth = data.length * (isExpansionNeeded ? 110 : minWidthMultiplier);
  const maxValue = Math.max(...data.map((d) => Number(d[valueKey])));
  const step = Math.ceil(maxValue / 4) || 4;
  const listOfValues = Array.from({ length: 5 }, (_, i) => i * step);

  const roundToNearestFive = (value) => {
    const pow = Math.pow(10, Math.floor(Math.log10(value)));
    return Math.floor(value / pow) * pow || 0;
  };

  return (
    <div style={{ height: '100%', width: '100%', overflowX: 'auto' }} className="no-scrollbar">
      <div style={{ width: `max(100%, ${minimumWidth}px)`, height: "100%" }}>
        <ChartContainer config={{}}>
          <ResponsiveContainer width="100%" height="80%">
            <BarChart
              data={data.map((d) => ({
                ...d,
                fill: d.isCurrent ? 'url(#specialGrad)' : 'url(#mainGradient)',
              }))}
              margin={{ top: 10, right: 20, bottom: 5, left }}
              barCategoryGap={40}
              barGap={0}
            >
              <defs>
                <linearGradient id="specialGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#FFEC9A" />
                  <stop offset="100%" stopColor="#00B2FF" />
                </linearGradient>
                <linearGradient id="mainGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#C79CD5" />
                  <stop offset="100%" stopColor="#6C22B5" />
                </linearGradient>
              </defs>

              <XAxis
                dataKey={dataKey}
                axisLine={false}
                tickLine={false}
                allowDataOverflow={false}
                interval={0}
                tick={({ x, y, payload }) => {
                  const isSpecial = data[payload?.index]?.isCurrent;
                  const payloadData = String(payload.value).split(' ');

                  return (
                    <g>
                      <text x={x} y={y + 10} textAnchor="middle" fontSize={12}
                      style={{
                        fill: isSpecial ? '#00B2FF' : '#94a3b8',
                      }}
                      >
                        {payloadData[0] + ' ' + (payloadData[1] || '')}
                      </text>
                      <text x={x} y={y + 25} textAnchor="middle" style={{
                        fill: isSpecial ? '#00B2FF' : '#94a3b8',
                      }} fontSize={12}>
                        {(payloadData[2] || '') + ' ' + (payloadData[3] || '')}
                      </text>
                      {isSpecial && <circle cx={x} cy={y + 20} r={3} fill="#00B2FF" />}
                    </g>
                  );
                }}
                dy={10}
              />

              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#94a3b8', fontSize: 12 }}
                domain={[0, tickFormatter === '%' ? 100 : step]}
                ticks={tickFormatter === '%' ? [0, 25, 50, 75, 100] : listOfValues}
                tickFormatter={(value) =>
                  nearest5 ? `${roundToNearestFive(value)}${tickFormatter}` : `${value}${tickFormatter}`
                }
                width={45}
                interval={0}
                style={{ textAnchor: 'end' }}
              />

              <Bar
                dataKey={valueKey}
                fill="url(#mainGradient)"
                radius={[10, 10, 10, 10]}
                barSize={20}
                background={<RoundedBackground />}
                fillOpacity={1}
                isAnimationActive={true}
                minPointSize={2}
              >
                <LabelList 
                  dataKey={valueKey} 
                  position="top" 
                  fill="white"
                  fontSize={12}
                  offset={10}
                />
              </Bar>
              <ChartTooltip
                content={
                  <ChartTooltipContent
                    defaultColor="#6C22B5"
                    dataKeys={[valueKey]}
                    dataLabels={[dataKey]}
                    tooltipFormatter={tooltipFormatter}
                  />
                }
              />
            </BarChart>
          </ResponsiveContainer>
        </ChartContainer>
      </div>
    </div>
  );
}
